import { showConfirm } from '../components/ConfirmDialog';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useDenseTableLabels } from '../hooks/useDenseTableLabels';
import { useTranslation, Trans } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import {
  apiGetApiKeys, apiCreateApiKey, apiRevokeApiKey, apiDeleteApiKey, apiGetApiKeyBands,
  type ApiKeyDTO,
} from '../api';
import { useWriteGate } from '../lib/writeGate';
import PageHeader from '../components/PageHeader';
import { withPageIcon } from '../lib/uiIcons';

/**
 * ApiKeysPage — admin page to manage Bearer API keys for external AI agents.
 * Each key can be scoped to a single band (the agent can only see/touch that
 * band) or to all bands the creator can access. The raw key is shown once.
 * (2026-08-11)
 */
export default function ApiKeysPage() {
  const denseRef = useRef<HTMLDivElement | null>(null);
  useDenseTableLabels(denseRef);
  const { user } = useAuth();
  const { t } = useTranslation();
  // Platform admin OR the read-only showcase (demo) account (sample data).
  const isAdmin = user?.global_role === 'superadmin' || !!user?.is_showcase;
  // Capability-aware write gate (lot A2). `/api/keys` is mounted AFTER the
  // server's global read-only guard (server/index.js:519-525; router at :602).
  // This page already hides itself from a non-admin (a global read-only account
  // never reaches the write UI); the gate freezes the showcase demo (which
  // renders the page).
  const writeGate = useWriteGate();
  const readOnlyNote = writeGate.readOnlyReason === 'global-read-only' && !writeGate.canWrite;

  const [keys, setKeys] = useState<ApiKeyDTO[]>([]);
  const [bands, setBands] = useState<Array<{ id: string; name: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'active' | 'revoked'>('active');

  const [label, setLabel] = useState('');
  const [scopeBand, setScopeBand] = useState('');
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setKeys(await apiGetApiKeys());
      setBands(await apiGetApiKeyBands());
    } catch (e: any) {
      setError(e?.message || t('apiKeys.failedLoad'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  if (!isAdmin) {
    return (
      <div className="page">
        <PageHeader title={withPageIcon('apiKeys.title', t('apiKeys.title'))} />
        <p className="empty-hint">{t('apiKeys.adminRequired')}</p>
      </div>
    );
  }

  const create = async () => {
    if (!writeGate.canWrite) return;
    if (!label.trim()) { alert(t('apiKeys.enterLabel')); return; }
    setCreating(true);
    setError(null);
    try {
      const created = await apiCreateApiKey({ label: label.trim(), band_id: scopeBand || undefined });
      setNewKey(created.key);
      setLabel('');
      setScopeBand('');
      await load();
    } catch (e: any) {
      setError(e?.message || t('apiKeys.failedCreate'));
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (k: ApiKeyDTO) => {
    if (!writeGate.canWrite) return;
    if (!(await showConfirm(t('apiKeys.revokeConfirm', { label: k.label })))) return;
    try {
      await apiRevokeApiKey(k.id);
      await load();
    } catch (e: any) {
      setError(e?.message || t('apiKeys.failedRevoke'));
    }
  };

  const del = async (k: ApiKeyDTO) => {
    if (!writeGate.canWrite) return;
    const ok = await showConfirm(
      k.revoked
        ? t('apiKeys.deleteConfirm', { label: k.label })
        : t('apiKeys.deleteActiveConfirm', { label: k.label }),
    );
    if (!ok) return;
    try {
      await apiDeleteApiKey(k.id);
      await load();
    } catch (e: any) {
      setError(e?.message || t('apiKeys.failedDelete'));
    }
  };

  const copy = () => { if (newKey) navigator.clipboard?.writeText(newKey); };

  const activeKeys = keys.filter((k) => !k.revoked);
  const revokedKeys = keys.filter((k) => k.revoked);
  const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleString() : t('apiKeys.never'));

  return (
    <div ref={denseRef} className="page" data-density="socle">
      <PageHeader
        title={withPageIcon('apiKeys.titleIcon', t('apiKeys.titleIcon'))}
        subtitle={t('apiKeys.subtitle')}
      />

      {readOnlyNote && (
        <p className="text-dim" style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)', margin: '0 0 12px' }}>
          {t('common.readOnlyAccount')}
        </p>
      )}

      {error && <p style={{ color: 'var(--danger)' }}>{error}</p>}

      <div className="card" style={{ maxWidth: 720, marginBottom: 20, padding: 16 }}>
        <h3 style={{ marginTop: 0 }}>{t('apiKeys.createKey')}</h3>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 200px' }}>
            <label>{t('apiKeys.label')}</label>
            <input className="form-input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder={t('apiKeys.labelPh')} />
          </div>
          <div style={{ flex: '1 1 200px' }}>
            <label>{t('apiKeys.scopeBand')}</label>
            <select className="form-input" value={scopeBand} onChange={(e) => setScopeBand(e.target.value)}>
              <option value="">{t('apiKeys.allBands')}</option>
              {bands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <button className="btn btn-primary" onClick={() => void create()} disabled={creating || !label.trim() || !writeGate.canWrite}>
            {creating ? t('apiKeys.creating') : t('apiKeys.createKeyBtn')}
          </button>
        </div>
        {newKey && (
          <div style={{ marginTop: 12, padding: 12, border: '1px solid var(--accent)', borderRadius: 8, background: 'var(--accent-tint)' }}>
            <strong>{t('apiKeys.newKeyTitle')}</strong>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
              <code style={{ wordBreak: 'break-all', flex: 1 }}>{newKey}</code>
              <button className="btn btn-secondary btn-small" onClick={copy}>{t('apiKeys.copy')}</button>
            </div>
            <p className="text-dim" style={{ fontSize: 'var(--font-xs)', margin: '6px 0 0' }}>
              <Trans i18nKey="apiKeys.keyHint" components={{ code: <code /> }} values={{ keyPreview: newKey.slice(0, 10) }}>
                Use it as <code>Authorization: Bearer {newKey.slice(0, 10)}…</code> or as the MCP server&apos;s <code>BANDZ_API_KEY</code>.
              </Trans>
            </p>
          </div>
        )}
      </div>

      {loading && <p className="text-dim">{t('apiKeys.loading')}</p>}
      {!loading && keys.length === 0 && (
        <p className="text-dim">{t('apiKeys.noKeys')}</p>
      )}
      {keys.length > 0 && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            <button className={`btn btn-small ${tab === 'active' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab('active')}>
              {t('apiKeys.tabActive', { count: activeKeys.length })}
            </button>
            <button className={`btn btn-small ${tab === 'revoked' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab('revoked')}>
              {t('apiKeys.tabRevoked', { count: revokedKeys.length })}
            </button>
          </div>

          {tab === 'active' && (
            <div className="table-wrapper" style={{ maxWidth: 720 }}>
              <table className="tbl-dense">
                <thead>
                  <tr><th>{t('apiKeys.colLabel')}</th><th>{t('apiKeys.colScope')}</th><th>{t('apiKeys.colCreated')}</th><th>{t('apiKeys.colLastUsed')}</th><th></th></tr>
                </thead>
                <tbody>
                  {activeKeys.map((k) => (
                    <tr key={k.id}>
                      <td>{k.label}</td>
                      <td>{k.band_name || t('apiKeys.allBandsScope')}</td>
                      <td className="text-dim">{fmtDate(k.created_at)}</td>
                      <td className="text-dim">{fmtDate(k.last_used_at)}</td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <button className="btn btn-danger btn-small" style={{ marginRight: 6 }} disabled={!writeGate.canWrite} onClick={() => void revoke(k)}>{t('apiKeys.revokeBtn')}</button>
                        <button className="btn btn-secondary btn-small" disabled={!writeGate.canWrite} onClick={() => void del(k)}>{t('apiKeys.deleteBtn')}</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {activeKeys.length === 0 && <p className="text-dim" style={{ marginTop: 12 }}>{t('apiKeys.noActive')}</p>}
            </div>
          )}

          {tab === 'revoked' && (
            <div className="table-wrapper" style={{ maxWidth: 720 }}>
              <table className="tbl-dense">
                <thead>
                  <tr><th>{t('apiKeys.colLabel')}</th><th>{t('apiKeys.colScope')}</th><th>{t('apiKeys.colCreated')}</th><th></th></tr>
                </thead>
                <tbody>
                  {revokedKeys.map((k) => (
                    <tr key={k.id}>
                      <td>{k.label}</td>
                      <td>{k.band_name || t('apiKeys.allBandsScope')}</td>
                      <td className="text-dim">{fmtDate(k.created_at)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <button className="btn btn-danger btn-small" disabled={!writeGate.canWrite} onClick={() => void del(k)}>{t('apiKeys.deleteBtn')}</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {revokedKeys.length === 0 && <p className="text-dim" style={{ marginTop: 12 }}>{t('apiKeys.noRevoked')}</p>}
            </div>
          )}
        </>
      )}

      <div className="card" style={{ maxWidth: 720, marginTop: 20, padding: 16 }}>
        <h3 style={{ marginTop: 0 }}>{t('apiKeys.integrate')}</h3>
        <p className="text-dim" style={{ fontSize: 'var(--font-sm)' }}>
          <Trans i18nKey="apiKeys.restApi" components={{ strong: <strong />, code: <code /> }}>
            <strong>REST API:</strong> call any <code>/api/…</code> endpoint with the header{' '}
            <code>Authorization: Bearer &lt;your-key&gt;</code>. The key&apos;s band scope limits what the agent can see.
          </Trans>
        </p>
        <pre style={{ background: 'var(--bg-elevated)', padding: 10, borderRadius: 6, fontSize: 'var(--font-xs)', overflow: 'auto' }}>
{`curl -H "Authorization: Bearer ***" https://YOUR_HOST/api/bands
curl -H "Authorization: Bearer ***" "https://YOUR_HOST/api/shows?band=<band_id>"`}
        </pre>
        <p className="text-dim" style={{ fontSize: 'var(--font-sm)' }}>
          <Trans i18nKey="apiKeys.mcpServer" components={{ strong: <strong />, code: <code /> }}>
            <strong>MCP server:</strong> add the Bandz MCP server to your agent host (Claude, OpenAI, Hermes…).
            See <code>server/mcp-server.mjs</code> for the config.
          </Trans>
        </p>
      </div>
    </div>
  );
}